import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      // Small delay before showing banner
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem("cookie_consent", JSON.stringify({
      essential: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const rejectNonEssential = () => {
    localStorage.setItem("cookie_consent", JSON.stringify({
      essential: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    // Disable analytics if rejected
    if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-slate-900 border-t border-slate-700 shadow-2xl"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          {/* Main Content */}
          <div className="flex items-start gap-4 flex-1">
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-blue-500/10 items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">We value your privacy</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                We use cookies to enhance your browsing experience and analyze our traffic. 
                By clicking "Accept All", you consent to our use of cookies.{" "}
                <button 
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  {showDetails ? "Hide details" : "Learn more"}
                </button>
              </p>
              
              {/* Expandable Details */}
              {showDetails && (
                <div className="mt-4 p-4 bg-slate-800 rounded-lg text-sm">
                  <div className="mb-3">
                    <h4 className="text-white font-medium mb-1">Essential Cookies</h4>
                    <p className="text-slate-400">
                      Required for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Analytics Cookies</h4>
                    <p className="text-slate-400">
                      Help us understand how visitors use our website to improve your experience.
                    </p>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Link 
                      to="/privacy-policy" 
                      className="text-blue-400 hover:text-blue-300 text-xs"
                    >
                      Privacy Policy
                    </Link>
                    <span className="text-slate-600">|</span>
                    <Link 
                      to="/cookie-policy" 
                      className="text-blue-400 hover:text-blue-300 text-xs"
                    >
                      Cookie Policy
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
            <Button
              data-testid="cookie-reject-btn"
              variant="outline"
              onClick={rejectNonEssential}
              className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-sm px-6"
            >
              Reject Non-Essential
            </Button>
            <Button
              data-testid="cookie-accept-btn"
              onClick={acceptAll}
              className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-6"
            >
              Accept All
            </Button>
          </div>

          {/* Close Button */}
          <button
            onClick={rejectNonEssential}
            className="absolute top-2 right-2 lg:relative lg:top-0 lg:right-0 p-2 text-slate-400 hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
